import {
  defaultSelectionLimit,
  defaultCustomQuestionsSelectionLimit,
} from '../../../constants';
import {
  type AvailableAnswer,
  QuestionDescription,
  QuestionSource,
  SelectionType,
  SelectionTypeView,
} from '../../../sdk';
import { AnswerRangeType } from '../../AnswerRange/AnswerRange';

import {
  ADD_SEARCH_QUESTION,
  ANSWER_RANGE_SELECT,
  type Actions,
  EMPTY_PREFERRED_ANSWER,
  SET_QUESTIONS_STATE,
  SET_VERSION_NUMBER,
  SHOW_QUESTIONS,
  type SelectedAnswerRangeAction,
  TOGGLE_ANSWER_SELECT,
  TOGGLE_QUESTION_SELECT,
  TOGGLE_ESSENTIAL_QUESTION_SELECT,
  DELETE_QUESTION,
  ADD_CUSTOM_QUESTION,
  SET_SELECTION_TYPE_VIEW,
  SET_QUESTIONNAIRE_HAS_QUESTIONS_STATE,
} from './actions';
import { getPreferredAnswers, libraryQuestionLimitReached } from './selectors';
import type { AvailableAnswerOrders, Question, State } from './types';

export const initialState: State = {
  questions: {},
  versionNumber: null,
  firstInteraction: true,
  selectionLimit: defaultSelectionLimit,
  customQuestionsSelectionLimit: defaultCustomQuestionsSelectionLimit,
  showQuestions: false,
  settings: {
    questionSectionHeading: false,
    showCertsyTooltip: false,
  },
};

export const reducer = (
  state: State = initialState,
  action: Actions,
): State => {
  switch (action.type) {
    case DELETE_QUESTION:
      const { [action.questionId]: _, ...questionsToKeep } = state.questions;

      return {
        ...state,
        questions: questionsToKeep,
      };
    case ADD_SEARCH_QUESTION:
      return {
        ...state,
        questions: {
          ...state.questions,
          [action.question.id]: {
            ...action.question,
            source: action.source,
            selected: !libraryQuestionLimitReached(state),
          },
        },
      };
    case ADD_CUSTOM_QUESTION:
      return {
        ...state,
        questions: {
          ...state.questions,
          [action.question.id]: {
            id: action.question.id,
            text: action.question.text,
            availableAnswers: action.question.options.map(
              (o): AvailableAnswer => ({
                id: o?.id || '',
                text: o?.text || '',
                order: o?.order || 0,
                preferred: true,
              }),
            ),
            preferredAnswers: {
              [SelectionTypeView.Answers]: [],
              [SelectionTypeView.SimplifiedAnswers]: [],
            },
            selected: true,
            source: QuestionSource.Custom,
            simplifiedAnswers: [],
            availableAnswerOrders: {},
            description: QuestionDescription.SingleSelectOnly,
            selectionType: action.question.questionType as any,
            answerType: action.question.questionType as any,
            order: action.order,
            selectionTypeView: SelectionTypeView.Answers,
            enableSelectionTypeToggle: false,
            certsyVerifiable: false,
            mandatory: false,
          } as Question,
        },
      };
    case SET_QUESTIONS_STATE:
      return {
        ...state,
        questions: action.questions,
        versionNumber: action.versionNumber,
        selectionLimit: action.selectionLimit,
        customQuestionsSelectionLimit: action.customQuestionsSelectionLimit,
        published: action.published,
        settings: {
          ...state.settings,
          questionSectionHeading: action.questionSectionHeading,
          showCertsyTooltip: action.showCertsyTooltip,
        },
        bundlingQuestionCategory: action.bundlingQuestionCategory,
      };
    case SET_VERSION_NUMBER:
      return {
        ...state,
        versionNumber: action.versionNumber,
      };
    case TOGGLE_QUESTION_SELECT:
      return {
        ...state,
        firstInteraction: false,
        questions: {
          ...state.questions,
          [action.questionId]: {
            ...state.questions[action.questionId],
            selected: !state.questions[action.questionId].selected,
            lastInteraction: new Date(Date.now()).toISOString(),
          },
        },
      };
    case TOGGLE_ESSENTIAL_QUESTION_SELECT:
      return {
        ...state,
        firstInteraction: false,
        questions: {
          ...state.questions,
          [action.questionId]: {
            ...state.questions[action.questionId],
            essential: !state.questions[action.questionId].essential,
          },
        },
      };
    case SET_SELECTION_TYPE_VIEW:
      return {
        ...state,
        firstInteraction: false,
        questions: {
          ...state.questions,
          [action.questionId]: {
            ...state.questions[action.questionId],
            selectionTypeView: action.selectionTypeView,
          },
        },
      };
    case TOGGLE_ANSWER_SELECT:
      const questionState = state.questions[action.questionId];
      const answerIds = getPreferredAnswers(questionState);
      const answerIndex = answerIds.indexOf(action.answerId);
      const isSelectingAnswer = answerIndex === -1;

      switch (questionState.selectionType) {
        case SelectionType.Radio:
          return {
            ...state,
            firstInteraction: false,
            questions: {
              ...state.questions,
              [action.questionId]: {
                ...questionState,
                preferredAnswers: assignPreferredAnswers(
                  questionState,
                  isSelectingAnswer ? [action.answerId] : [],
                ),
              },
            },
          };
        case SelectionType.Checkbox:
          return {
            ...state,
            firstInteraction: false,
            questions: {
              ...state.questions,
              [action.questionId]: {
                ...state.questions[action.questionId],
                preferredAnswers: assignPreferredAnswers(
                  questionState,
                  isSelectingAnswer
                    ? [...answerIds, action.answerId]
                    : [
                        ...answerIds.slice(0, answerIndex),
                        ...answerIds.slice(answerIndex + 1),
                      ],
                ),
              },
            },
          };
        default:
          return state;
      }
    case ANSWER_RANGE_SELECT:
      return {
        ...state,
        firstInteraction: false,
        questions: {
          ...state.questions,
          [action.questionId]: {
            ...state.questions[action.questionId],
            preferredAnswers: assignPreferredAnswers(
              state.questions[action.questionId],
              updatePreferredAnswerRange(
                action,
                state.questions[action.questionId],
              ),
            ),
          },
        },
      };
    case SHOW_QUESTIONS:
      return {
        ...state,
        showQuestions: true,
      };
    case SET_QUESTIONNAIRE_HAS_QUESTIONS_STATE:
      return {
        ...state,
        questionnaireHasQuestions: action.questionnaireHasQuestions,
      };
    default:
      return state;
  }
};

const assignPreferredAnswers = (
  question: Question,
  preferredAnswers: string[],
): { [key in SelectionTypeView]: string[] } => ({
  ...question.preferredAnswers,
  [question.selectionTypeView]: preferredAnswers,
});

const getExistingMaximumPreferredAnswer = (
  action: SelectedAnswerRangeAction,
  preferredAnswers: string[],
  availableAnswerOrders: AvailableAnswerOrders,
): string => {
  const minimumOrder = availableAnswerOrders[action.answerId] || 0;
  const existingMaximumOrder =
    availableAnswerOrders[preferredAnswers[AnswerRangeType.Maximum]] || 0;

  const isExistingSingleMinimumValue =
    preferredAnswers[AnswerRangeType.Minimum] ===
    preferredAnswers[AnswerRangeType.Maximum];

  return minimumOrder >= existingMaximumOrder || isExistingSingleMinimumValue
    ? EMPTY_PREFERRED_ANSWER
    : preferredAnswers[AnswerRangeType.Maximum];
};

const updatePreferredAnswerRange = (
  action: SelectedAnswerRangeAction,
  question: Question,
): string[] => {
  const preferredAnswerIds = getPreferredAnswers(question);
  const preferredAnswers: string[] =
    preferredAnswerIds.length === 2
      ? preferredAnswerIds
      : [EMPTY_PREFERRED_ANSWER, EMPTY_PREFERRED_ANSWER];

  // TODO: if single preferred answer, just store ['123'];
  // simplify isError validation: for range, must have 2 items in array

  const minimum =
    action.rangeType === AnswerRangeType.Minimum
      ? action.answerId
      : preferredAnswers[AnswerRangeType.Minimum];

  const maximum =
    action.rangeType === AnswerRangeType.Maximum
      ? action.answerId
      : getExistingMaximumPreferredAnswer(
          action,
          preferredAnswers,
          question.availableAnswerOrders,
        );

  return [minimum, maximum];
};
